import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '../context/AuthContext';
import { PRICING } from '../constants/pricing';
import { InstallPromptModal, useShouldShowInstallRow } from '../components/InstallPrompt';

const BLUE  = 'var(--signal)';
const RED   = 'var(--stop)';
const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';

function StickyHeader({ navigate }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      <span
        onClick={() => navigate('/dashboard')}
        style={{ fontSize: 18, color: TEXT2, cursor: 'pointer', lineHeight: 1, paddingRight: 4, userSelect: 'none' }}
      >
        ←
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

// Plan / subscription state. access_tier === 'beta' grants access on its own,
// independently of subscription_status, so a beta member is entitled and
// unsubscribed at the same time — the two flags are checked separately because
// neither one alone answers "is this person being asked to subscribe".
function PlanSection({ user, navigate }) {
  const isBeta = user?.access_tier === 'beta';
  const isSubscribed = user?.subscription_status === 'active';

  // stripe_price_id is on /auth/me; matching it against the two configured
  // price IDs is the only way to name the plan — anything unrecognised omits
  // the sub-line rather than guessing.
  const priceId = user?.stripe_price_id ?? null;
  let planName = null;
  if (priceId && priceId === process.env.REACT_APP_STRIPE_PRICE_MONTHLY) planName = 'Monthly plan';
  else if (priceId && priceId === process.env.REACT_APP_STRIPE_PRICE_ANNUAL) planName = 'Annual plan';

  let title;
  let subLine;
  let ctaLabel = null;
  if (isSubscribed) {
    title = 'Subscribed';
    subLine = planName;
  } else if (isBeta) {
    title = 'First year offer';
    subLine = `$${PRICING.monthly.intro}/mo for your first ${PRICING.monthly.introMonths} months, then $${PRICING.monthly.base}/mo.`;
    ctaLabel = 'Claim intro rate';
  } else {
    title = 'Free';
    subLine = 'Subscribe to keep your reserve, cash flow, and expense tracking.';
    ctaLabel = 'See plans';
  }

  function handleClick() {
    posthog.capture('settings_plan_cta_clicked', { plan_state: isBeta ? 'beta' : 'free' });
    navigate('/pricing');
  }

  return (
    <>
      <p style={{
        margin: '20px 0 9px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--mute)',
      }}>
        Plan
      </p>
      <div style={{
        backgroundColor: 'var(--card)',
        boxShadow: 'var(--sh-card)',
        borderRadius: 'var(--r-card)',
        padding: 16,
        marginBottom: 12,
      }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{title}</p>
        {subLine && (
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--slate)' }}>{subLine}</p>
        )}
        {ctaLabel && (
          <button onClick={handleClick} style={{
            width: '100%', marginTop: 14,
            backgroundColor: 'var(--signal)', color: 'var(--on-signal)',
            border: 'none', borderRadius: 14, padding: 14,
            fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
          }}>
            {ctaLabel}
          </button>
        )}
      </div>
    </>
  );
}

function SectionLabel({ text }) {
  return (
    <p style={{
      margin: '0 0 6px',
      fontSize: 11, fontWeight: 500,
      color: TEXT3, textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>
      {text}
    </p>
  );
}

function SettingsCard({ children }) {
  return (
    <div style={{
      backgroundColor: 'var(--card)',
      boxShadow: 'var(--sh-card)',
      borderRadius: 'var(--r-card)',
      padding: '0 16px',
      marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function Row({ label, onClick, danger, last }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 0',
        borderBottom: last ? 'none' : '0.5px solid rgba(0,0,0,0.07)',
        cursor: 'pointer',
      }}
    >
      <span style={{
        fontSize: 14,
        color: danger ? RED : TEXT1,
      }}>
        {label}
      </span>
      {!danger && (
        <span style={{ fontSize: 20, color: TEXT3, lineHeight: 1 }}>›</span>
      )}
    </div>
  );
}

export default function Settings({ embedded = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const showInstallRow = useShouldShowInstallRow();
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Embedded (the You tab) drops the sticky header, page shell and title —
  // DetailScreen supplies all three. Standalone /settings keeps them.
  const body = (
    <>
      {!embedded && (
        <h1 style={{ margin: '0 0 20px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>
          Settings
        </h1>
      )}

        {/* User info */}
        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)',
          padding: 16,
          marginBottom: 20,
        }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: TEXT1 }}>
            {user?.first_name ?? user?.firstName ?? ''}
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: TEXT3 }}>
            {user?.email ?? ''}
          </p>
        </div>

        <PlanSection user={user} navigate={navigate} />

        {/* Tax & Income */}
        <SectionLabel text="Tax & Income" />
        <SettingsCard>
          <Row label="Tax setup"      onClick={() => navigate('/settings/tax')} />
          <Row label="Income pattern" onClick={() => navigate('/settings/curve')} last />
        </SettingsCard>

        {/* IRS payment guide */}
        <SettingsCard>
          <Row label="IRS payment guide" onClick={() => navigate('/irs-payment')} last />
        </SettingsCard>

        {/* Permanent re-entry point for the onboarding install prompt — the
            only way back in for anyone who dismissed it there. Hidden once
            installed or on a platform that can't install (desktop). */}
        {showInstallRow && (
          <SettingsCard>
            <Row label="Add to Home Screen" onClick={() => setShowInstallModal(true)} last />
          </SettingsCard>
        )}

        {/* Log out */}
        <SettingsCard>
          <Row label="Log out" onClick={logout} danger last />
        </SettingsCard>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'var(--mute)' }}>
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=cd80ded6-7249-43fa-935c-0093b03f9dde"
             target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)', marginRight: 12 }}>
            Privacy Policy
          </a>
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=479a6457-8426-4afa-a7b1-f879c691825d"
             target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)', marginRight: 12 }}>
            Terms of Service
          </a>
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=8643b538-56b9-43a2-8f5e-62e7fb7f230f"
             target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)', marginRight: 12 }}>
            Cookie Policy
          </a>
          <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=9b4eba9c-71b4-41bd-b5cf-5c6b87a7e19e"
             target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)' }}>
            Disclaimer
          </a>
          <button
            className="termly-display-preferences"
            onClick={() => { if (window.displayPreferenceModal) window.displayPreferenceModal(); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--mute)',
              fontSize: 11,
              marginLeft: 12,
              padding: 0,
              textDecoration: 'underline'
            }}
          >
            Consent Preferences
          </button>
        </div>
    </>
  );

  return (
    <>
      {embedded ? body : (
        <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
          <StickyHeader navigate={navigate} />
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
            {body}
          </div>
        </div>
      )}
      {showInstallModal && (
        <InstallPromptModal onClose={() => setShowInstallModal(false)} />
      )}
    </>
  );
}
